/**
 * Instacut — shared availability logic.
 * =====================================
 * Single source of truth for calendar-availability calculations used by:
 *   • /diary (shop-owner Diary — ghost slots, booking-modal slot picker)
 *   • /book/:subdomain (public customer booking flow)
 *
 * Keeping both consumers on this file guarantees the customer never
 * sees a slot the shop-owner side considers unavailable, and vice-versa.
 */

// -------- Time helpers ----------------------------------------------------

/** Parse a wall-clock time string ("HH:MM" or "HH:MM:SS") to minutes past midnight. */
export const parseWallMin = (str) => {
  if (!str) return null;
  const [hh, mm] = String(str).split(':').map(Number);
  if (Number.isNaN(hh)) return null;
  return hh * 60 + (mm || 0);
};

/** Format minutes past midnight to "HH:MM". */
export const formatWallMin = (min) => {
  const hh = Math.floor(min / 60);
  const mm = min % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

// -------- Barber hours ----------------------------------------------------

/**
 * Return the working-hours window for a barber on a given calendar date.
 * @param {Object} hoursByBarber Map of barberId → Array<barber_hours row>
 * @param {string} barberId
 * @param {Date}   date
 * @returns {{open:boolean, startMin?:number, endMin?:number}}
 */
export const hoursForDate = (hoursByBarber, barberId, date) => {
  const weekday = date.getDay();
  const rows = hoursByBarber?.[barberId] || [];
  const row = rows.find(r => r.weekday === weekday);
  if (!row || row.is_open === false) return { open: false };
  const startMin = parseWallMin(row.start_time);
  const endMin   = parseWallMin(row.end_time);
  if (startMin == null || endMin == null || endMin <= startMin) return { open: false };
  return { open: true, startMin, endMin };
};

// -------- Date exceptions (holidays / force-open days) --------------------

/**
 * ISO YYYY-MM-DD key for a Date, in local timezone.
 * Used to match date-exception rows against calendar days.
 */
export const dateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Apply a barber's date-exception (if any) on top of their weekly hours
 * for a given date. Returns the effective hours window.
 *
 * @param {{open, startMin?, endMin?}} weeklyHours  From `hoursForDate`
 * @param {Object|undefined} exception   Row from `barber_date_exceptions`
 *   with `is_closed`, `start_time`, `end_time`.
 * @returns {{open, startMin?, endMin?}}
 */
export const applyDateException = (weeklyHours, exception) => {
  if (!exception) return weeklyHours;
  if (exception.is_closed) return { open: false };
  const startMin = parseWallMin(exception.start_time);
  const endMin   = parseWallMin(exception.end_time);
  if (startMin == null || endMin == null || endMin <= startMin) return { open: false };
  return { open: true, startMin, endMin };
};


// -------- Slot generation -------------------------------------------------

/**
 * Compute the free time-slot starts for a given (barber, date, service).
 * A slot at minute `t` is free iff:
 *   • `t + durationMin <= endMin`
 *   • no existing booking overlaps [t, t+durationMin)
 *
 * @param {Object} opts
 * @param {{open, startMin, endMin}} opts.hours    From `hoursForDate`
 * @param {number} opts.durationMin                Service length in minutes
 * @param {Array}  opts.bookings                   Bookings that already
 *   overlap this date+barber. Each must expose `starts_at` and `ends_at`
 *   as ISO strings OR wall-clock minute pairs `{startMin, endMin}`.
 * @param {number} [opts.stepMin=15]               Slot-start granularity
 * @param {number} [opts.bufferMin=0]              Extra minutes required
 *   AFTER every busy interval before the next slot can start. Applies
 *   to bookings only (not breaks). 0 = no gap (previous behaviour).
 * @param {Date}   [opts.date]                     Only needed if bookings
 *   are ISO strings — used to convert to minute-of-day.
 * @param {number} [opts.minStartMin]              Optional earliest start
 *   (e.g. lead time from now). Slots earlier than this are excluded.
 * @param {boolean} [opts.anchorToBookingEnds=false] When true, ALSO emit
 *   candidate starts aligned to the end of each preceding booking
 *   (rounded up to `anchorQuantumMin`). This closes the gap where a
 *   45-min booking ending at 10:15 with a 30-min grid would otherwise
 *   push the next customer slot to 10:30 instead of 10:15.
 * @param {number}  [opts.anchorQuantumMin=5]      Rounding quantum used
 *   for booking-end anchors. 5 matches typical salon-industry granularity.
 * @returns {number[]} Array of slot-start minutes past midnight.
 */
export const computeAvailableSlots = ({
  hours,
  durationMin,
  bookings = [],
  breaks = [],
  stepMin = 15,
  bufferMin = 0,
  date,
  minStartMin,
  anchorToBookingEnds = false,
  anchorQuantumMin = 5,
}) => {
  if (!hours?.open || !durationMin || durationMin <= 0) return [];

  const buf = Math.max(0, Number(bufferMin) || 0);

  // Normalize bookings + breaks into a single busy list of { s, e, leadBuf }
  // minute pairs. `leadBuf` mirrors what create_public_booking does at INSERT
  // time — bookings get a symmetric buffer (buf added to both head and tail),
  // breaks do not. Without the leading buf the wizard offered slots ending
  // exactly at an existing booking's start on any barber with a non-zero
  // buffer; the RPC then rejected the INSERT with "already booked". See
  // Migration B tstzrange(starts_at - buf, ends_at + buf).
  const normalizeBooking = (b) => {
    if (typeof b.startMin === 'number' && typeof b.endMin === 'number') {
      return { s: b.startMin, e: b.endMin + buf, leadBuf: buf };
    }
    const s = new Date(b.starts_at);
    const e = new Date(b.ends_at);
    return {
      s: s.getHours() * 60 + s.getMinutes(),
      e: e.getHours() * 60 + e.getMinutes() + buf,
      leadBuf: buf,
    };
  };
  const parseWall = (t) => {
    if (t == null) return null;
    const [hh, mm] = String(t).split(':').map(Number);
    return hh * 60 + (mm || 0);
  };
  const bks = [
    ...bookings.map(normalizeBooking),
    ...breaks.map(br => ({
      s: parseWall(br.start_time),
      e: parseWall(br.end_time),
      leadBuf: 0,   // breaks are exact intervals — no buffer, same as the RPC
    })),
  ]
    .filter(x => Number.isFinite(x.s) && Number.isFinite(x.e))
    .sort((a, b) => a.s - b.s);

  const gridStart = hours.startMin;
  const ceil      = hours.endMin;
  // Anything earlier than minCutoff is either before opening or
  // inside the barber's lead-time buffer — filtered out AFTER the
  // grid is built, never used to rebase the grid itself.
  const minCutoff = Math.max(gridStart, minStartMin || 0);

  // Build the candidate start set. Fixed-grid walk anchored to
  // opening time so slot starts always align to the barber's
  // configured `slot_interval_minutes`. If we anchored to minCutoff
  // instead, a customer opening the page at 09:14 with a 30-min grid
  // and 0h lead time would see slots at 09:15, 09:45, ..., 13:15,
  // 13:45 — off the shop's grid and creating awkward gaps next to
  // earlier :00/:30 bookings on the day.
  // (Regression: htrimzz + Haroon Raza + Muzzy, 2026-09-24.)
  const candidates = new Set();
  for (let t = gridStart; t + durationMin <= ceil; t += stepMin) {
    if (t >= minCutoff) candidates.add(t);
  }
  if (anchorToBookingEnds) {
    const q = Math.max(1, Number(anchorQuantumMin) || 5);
    for (const bk of bks) {
      // bk.e already includes the barber's buffer; round UP to quantum
      // so the anchor lands on a customer-friendly minute (e.g. :15).
      const anchor = Math.ceil(bk.e / q) * q;
      if (anchor >= minCutoff && anchor + durationMin <= ceil) {
        candidates.add(anchor);
      }
    }
  }

  const out = [...candidates].sort((a, b) => a - b).filter(t =>
    !bks.some(bk => bk.s - bk.leadBuf < t + durationMin && bk.e > t)
  );
  return out;
};

// -------- Interval-overlap check (used server-side and modal Save) -------

/**
 * Client-side interval-overlap helper. `existing.starts_at < newEnd
 * AND existing.ends_at > newStart` — the same predicate the fixed
 * modal Save flow uses to double-check even after slot-picker.
 */
export const bookingsOverlap = (existing, newStart, newEnd) => {
  const s = new Date(existing.starts_at);
  const e = new Date(existing.ends_at);
  return s < newEnd && e > newStart;
};
